import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Alert } from 'antd';
import { ISurveyCohort } from '../../services/nestjs-base-api/types';

const { TextArea } = Input;
const { Option } = Select;

interface CohortFormModalProps {
    visible: boolean;
    cohort: ISurveyCohort | null;
    systemCohorts: ISurveyCohort[];
    onCancel: () => void;
    onSubmit: (values: any) => Promise<void>;
}

const CohortFormModal: React.FC<CohortFormModalProps> = ({
    visible,
    cohort,
    systemCohorts,
    onCancel,
    onSubmit
}) => {
    const [form] = Form.useForm();
    const isEditMode = !!cohort;

    // Sort system cohorts by ID to ensure cohort 1 is first
    const sortedSystemCohorts = [...systemCohorts].sort((a, b) => a.id - b.id);

    useEffect(() => {
        if (visible) {
            if (cohort) {
                // Edit mode - populate form
                form.setFieldsValue({
                    name: cohort.name,
                    displayName: cohort.displayName,
                    description: cohort.description,
                    parentCohortId: cohort.parentCohort?.id,
                });
            } else {
                // Create mode - reset form
                form.resetFields();
            }
        }
    }, [visible, cohort, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await onSubmit(values);
            form.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    return (
        <Modal
            title={isEditMode ? 'Edit Customer Cohort' : 'Create Customer Cohort'}
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={isEditMode ? 'Update' : 'Create'}
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark="optional"
            >
                <Form.Item
                    name="displayName"
                    label="Display Name"
                    rules={[
                        { required: true, message: 'Please enter a display name' },
                        { max: 100, message: 'Display name must be less than 100 characters' }
                    ]}
                    tooltip="The name shown in the UI and reports"
                >
                    <Input
                        placeholder="e.g., Product A Buyers, Premium Subscribers"
                        maxLength={100}
                        showCount
                    />
                </Form.Item>

                <Form.Item
                    name="name"
                    label="Internal Name"
                    rules={[
                        { required: true, message: 'Please enter an internal name' },
                        { max: 255, message: 'Internal name must be less than 255 characters' }
                    ]}
                    tooltip="Used for technical identification (can be same as display name)"
                >
                    <Input
                        placeholder="e.g., Bought Product A"
                        maxLength={255}
                    />
                </Form.Item>

                {!isEditMode && (
                    <Alert
                        message="About Parent Cohorts"
                        description={
                            <>
                                Custom cohorts need to be derived from a parent cohort for benchmarking purposes.
                                Since other businesses don't have your specific custom cohorts, we compare your results
                                to the broader parent cohort category.
                                {sortedSystemCohorts.length > 0 && (
                                    <>
                                        <br /><br />
                                        <strong>Tip:</strong> If in doubt, select "{sortedSystemCohorts[0]?.displayName || 'Past Customers'}"
                                        as it's the most commonly used parent cohort.
                                    </>
                                )}
                            </>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item
                    name="parentCohortId"
                    label="Parent Cohort"
                    rules={[{ required: true, message: 'Please select a parent cohort' }]}
                    tooltip="Select which system cohort this custom cohort belongs to"
                >
                    <Select
                        placeholder="Select system cohort"
                        optionLabelProp="label"
                    >
                        {sortedSystemCohorts.map((sysCohort) => (
                            <Option
                                key={sysCohort.id}
                                value={sysCohort.id}
                                label={sysCohort.displayName}
                            >
                                <div>
                                    <div style={{ fontWeight: 500 }}>{sysCohort.displayName}</div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                        {sysCohort.description}
                                    </div>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Description"
                    rules={[
                        { max: 500, message: 'Description must be less than 500 characters' }
                    ]}
                    tooltip="Explain what makes this cohort unique and how it's defined"
                >
                    <TextArea
                        placeholder="e.g., Customers who purchased Product A within the last 90 days"
                        rows={4}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Form>

            {!isEditMode && (
                <div style={{
                    marginTop: 16,
                    padding: 12,
                    backgroundColor: '#f0f9ff',
                    borderRadius: 6,
                    fontSize: '13px',
                    color: '#1e40af'
                }}>
                    <strong>Note:</strong> After creating this cohort, you'll need to manually manage the customer list
                    for this cohort in your database or through your integration system.
                </div>
            )}
        </Modal>
    );
};

export default CohortFormModal;
